import { Button } from "primereact/button";
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { RiVolumeUpFill, RiVolumeMuteFill, RiPlayFill, RiPauseFill } from "@remixicon/react";
import emptyImage from "../assets/question-fallback.png";
import LSVariables from "../configs/LS_variables.json";
import { Chapter } from "../interfaces/chapter.interface";
import { Question } from "../interfaces/question.interface";
import { AuthService } from "../services/AuthService";
import { ChapterService } from "../services/ChapterService";
import { FileService } from "../services/FileService";
import {
  deleteSavedState,
  getSavedState,
  setSavedState,
} from "../utils/LocalStorage.utils";

const TestPage: React.FC = () => {
  const history = useHistory();
  const chapterService = new ChapterService();
  const authService = new AuthService();
  const fileService = new FileService();

  const [currentChapter, setCurrentChapter] = useState<Chapter | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [questionNumber, setQuestionNumber] = useState<number>(1);
  const [userAnswers, setUserAnswers] = useState<string[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [overallProgress, setOverallProgress] = useState<number>(0);
  const [completedChapters, setCompletedChapters] = useState<number>(0);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [shuffledQuestions, setShuffledQuestions] = useState<Question[]>([]);
  const [hasOverflow, setHasOverflow] = useState<boolean>(false);
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [showVoiceWarning, setShowVoiceWarning] = useState<boolean>(false);
  const [autoplay, setAutoplay] = useState<boolean>(() => {
    const saved = getSavedState('autoplay_enabled');
    return saved !== null ? saved : true;
  });

  const answersContainerRef = useRef<HTMLDivElement>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Fisher-Yates shuffle algorithm
  const shuffleArray = (array: any[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    const populateVoices = () => {
      const availableVoices = speechSynthesis.getVoices();
      if (availableVoices.length > 0) {
        setVoices(availableVoices);
        const frenchVoice = availableVoices.find(
          (voice) => voice.lang === "fr-FR"
        );
        if (!frenchVoice) {
          setShowVoiceWarning(true);
        }
      }
    };

    populateVoices();
    if (speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = populateVoices;
    }
    const fallbackTimeout = setTimeout(populateVoices, 1000);

    return () => {
      clearTimeout(fallbackTimeout);
      speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  useEffect(() => {
    const driver = authService.getUser();
    chapterService
      .getChaptersDetails(driver.transporter, driver._id)
      .then((res: any) => {
        const fetchedChapters: Chapter[] = res.data.data;
        setChapters(fetchedChapters);

        const savedChapter = getSavedState(LSVariables.current_chapter);
        if (savedChapter) {
          // Check if the saved chapter is already completed
          if (driver.completedChapters?.includes(savedChapter._id)) {
            // Find the next uncompleted chapter
            const nextUncompletedChapter = fetchedChapters.find(
              (chapter) => !driver.completedChapters?.includes(chapter._id)
            );

            if (nextUncompletedChapter) {
              setCurrentChapter(nextUncompletedChapter);
              const shuffled = shuffleArray(nextUncompletedChapter.questions);
              setShuffledQuestions(shuffled);
              setCurrentQuestion(shuffled[0]);
              const completedCount = driver.completedChapters?.length || 0;
              setCompletedChapters(completedCount);
              setOverallProgress(
                (completedCount / fetchedChapters.length) * 100
              );
            } else {
              // All chapters completed
              handleTestCompletion();
            }
          } else {
            // Continue with saved chapter as it's not completed
            setCurrentChapter(savedChapter);
            const shuffled = shuffleArray(savedChapter.questions);
            setShuffledQuestions(shuffled);
            setCurrentQuestion(shuffled[0]);
            const completedCount = driver.completedChapters?.length || 0;
            setCompletedChapters(completedCount);
            setOverallProgress((completedCount / fetchedChapters.length) * 100);
          }
        } else {
          // Find the first uncompleted chapter
          const firstUncompletedChapter = fetchedChapters.find(
            (chapter) => !driver.completedChapters?.includes(chapter._id)
          );

          if (firstUncompletedChapter) {
            setCurrentChapter(firstUncompletedChapter);
            const shuffled = shuffleArray(firstUncompletedChapter.questions);
            setShuffledQuestions(shuffled);
            setCurrentQuestion(shuffled[0]);
            const completedCount = driver.completedChapters?.length || 0;
            setCompletedChapters(completedCount);
            setOverallProgress((completedCount / fetchedChapters.length) * 100);
          } else {
            // All chapters completed
            handleTestCompletion();
          }
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [currentQuestion]);

  useEffect(() => {
    return () => {
      if (speechSynthesis.speaking) {
        speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (autoplay && currentQuestion && voices.length > 0) {
      const timeoutId = setTimeout(() => {
        speakQuestion();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion, autoplay]);

  const checkOverflow = () => {
    if (answersContainerRef.current) {
      const { scrollHeight, clientHeight } = answersContainerRef.current;
      setHasOverflow(scrollHeight > clientHeight);
    }
  };

  const handleAnswerSelection = (answer: string) => {
    setUserAnswers((prevAnswers) => {
      if (prevAnswers.includes(answer)) {
        return prevAnswers.filter((a) => a !== answer);
      } else {
        return [...prevAnswers, answer];
      }
    });
    setShowErrorMessage(false);
  };

  const handleNextQuestion = async () => {
    if (currentChapter && currentQuestion) {
      const correctAnswers = currentQuestion.correctAnswers;
      const isAnswerCorrect =
        userAnswers.length === correctAnswers.length &&
        userAnswers.every((answer) => correctAnswers.includes(answer));

      if (isAnswerCorrect) {
        if (speechSynthesis.speaking) {
          speechSynthesis.cancel();
          setIsSpeaking(false);
        }
        const nextQuestionIndex =
          shuffledQuestions.findIndex((q) => q._id === currentQuestion._id) + 1;
        if (nextQuestionIndex < shuffledQuestions.length) {
          setCurrentQuestion(shuffledQuestions[nextQuestionIndex]);
          setQuestionNumber(questionNumber + 1);
          setUserAnswers([]);
        } else {
          // Handle chapter completion
          const driver = authService.getUser();
          try {
            // Mark the current chapter as completed
            const updatedDriver = await chapterService.markChapterAsCompleted(
              driver._id,
              currentChapter._id
            );
            // Update the user in local storage with the new completedChapters
            authService.setUser(updatedDriver.data.data);

            // Find the next uncompleted chapter
            const nextUncompletedChapter = chapters.find(
              (chapter) =>
                !updatedDriver.data.data.completedChapters?.includes(
                  chapter._id
                )
            );

            if (nextUncompletedChapter) {
              setCurrentChapter(nextUncompletedChapter);
              const shuffled = shuffleArray(nextUncompletedChapter.questions);
              setShuffledQuestions(shuffled);
              setCurrentQuestion(shuffled[0]);
              setQuestionNumber(1);
              setUserAnswers([]);
              const newCompletedChapters =
                updatedDriver.data.data.completedChapters?.length || 0;
              setCompletedChapters(newCompletedChapters);
              setOverallProgress(
                (newCompletedChapters / chapters.length) * 100
              );
            } else {
              // All chapters completed
              handleTestCompletion();
            }
          } catch (error) {
            console.error("Error marking chapter as completed:", error);
          }
        }
      } else {
        setShowErrorMessage(true);
      }
    }
  };

  const handleTestCompletion = () => {
    deleteSavedState(LSVariables.current_chapter);
    setSavedState(true, LSVariables.test_finished);

    // Redirect to results page
    history.push("/resultat");
  };

  const scrollToBottom = () => {
    if (answersContainerRef.current) {
      answersContainerRef.current.scrollTop =
        answersContainerRef.current.scrollHeight;
    }
  };

  const speakQuestion = () => {
    if (currentQuestion) {
      let textToSpeak = `${currentQuestion.questionAnnouncement}. A!. ${currentQuestion.answerA}. B!. ${currentQuestion.answerB}`;
      if (currentQuestion.answerC) {
        textToSpeak += `. C!. ${currentQuestion.answerC}`;
      }
      if (currentQuestion.answerD) {
        textToSpeak += `. D!. ${currentQuestion.answerD}`;
      }
      
      const utterance = new SpeechSynthesisUtterance(textToSpeak);

      const frenchVoice = voices.find((voice) => voice.lang === "fr-FR");

      if (frenchVoice) {
        utterance.voice = frenchVoice;
      } else {
        utterance.lang = "fr-FR";
      }

      utterance.onend = () => {
        setIsSpeaking(false);
      };
      utteranceRef.current = utterance;
      speechSynthesis.speak(utterance);
      setIsSpeaking(true);
    }
  };

  const handleToggleSpeech = () => {
    if (isSpeaking) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
    } else {
      speakQuestion();
    }
  };

  const handleAutoplayToggle = () => {
    const newAutoplayState = !autoplay;
    setAutoplay(newAutoplayState);
    setSavedState(newAutoplayState, 'autoplay_enabled');
  };

  if (!currentChapter || !currentQuestion) {
    return <div>Chargement...</div>;
  }

  return (
    <main className="flex flex-grow flex-col p-4 md:p-6">
      <div
        id="quiz-interface"
        className="bg-white rounded-lg p-4 md:p-6 max-h-full md:max-h-[calc(100vh-225px)] mb-[100px] flex flex-col lg:flex-row h-full border border-gray-200"
      >
        <div className="flex flex-col lg:flex-row flex-1">
          <div className="w-full lg:w-1/2 lg:pr-4 flex flex-col mb-4 lg:mb-0">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg md:text-xl font-bold">
                Question {questionNumber} sur {currentChapter.questions.length}
              </h2>
              <div className="flex gap-2">
                <Button
                  className="p-button-rounded p-button-text flex items-center gap-2"
                  onClick={handleAutoplayToggle}
                  tooltip={autoplay ? "Désactiver la lecture automatique" : "Activer la lecture automatique"}
                  tooltipOptions={{ position: "top" }}
                >
                  {autoplay ? <RiVolumeUpFill size={16} /> : <RiVolumeMuteFill size={16} />}
                  <span>Lecture Automatique</span>
                </Button>
                <Button
                  className="p-button-rounded p-button-text flex items-center gap-2"
                  onClick={handleToggleSpeech}
                  tooltip={
                    showVoiceWarning
                      ? "Aucune voix française n'a été trouvée. La lecture audio peut utiliser une autre langue."
                      : "Rejouer la question"
                  }
                  tooltipOptions={{ position: "top" }}
                >
                  {isSpeaking ? <RiPauseFill size={16} /> : <RiPlayFill size={16} />}
                  <span>{isSpeaking ? "Pause" : "Rejouer"}</span>
                </Button>
              </div>
            </div>
            <div className="w-full bg-neutral-200 rounded-full h-2 mb-4">
              <div
                className="bg-primary h-2 rounded-full"
                style={{
                  width: `${
                    (questionNumber / currentChapter.questions.length) * 100
                  }%`,
                }}
              ></div>
            </div>
            <p className="text-base md:text-lg mb-4">
              {currentQuestion.questionAnnouncement}
            </p>
            <div
              ref={answersContainerRef}
              className={`space-y-2 lg:space-y-4 mb-4 xl:mb-6 overflow-y-auto pe-4 md:pe-12 relative ${
                hasOverflow ? "" : ""
              }`}
            >
              {["A", "B", "C", "D"].map((letter) => {
                const answerKey = `answer${letter}` as keyof Question;
                const answer = currentQuestion[answerKey];
                if (!answer) return null;

                return (
                  <label
                    key={letter}
                    className="answer flex items-center p-3 md:p-4 w-full border border-neutral-200 rounded-2xl hover:border-primary hover:bg-blue-100/10 cursor-pointer transition duration-300"
                  >
                    <input
                      type="checkbox"
                      name="answer"
                      className="w-5 h-5 md:w-6 md:h-6 cursor-pointer appearance-none border border-gray-300 rounded-md mr-2 checked:bg-no-repeat checked:bg-center checked:border-indigo-500 checked:bg-primary focus:!outline-none active:!outline-none focus:ring-0 focus:border-none active:border-none"
                      checked={userAnswers.includes(answerKey)}
                      onChange={() => handleAnswerSelection(answerKey)}
                    />
                    <span className="text-sm md:text-base w-full">
                      {answer}
                    </span>
                  </label>
                );
              })}
              {hasOverflow && (
                <div className="sticky bottom-0 right-0 translate-x-12 flex justify-end pb-2 pr-2">
                  <button
                    onClick={scrollToBottom}
                    className="bg-primary text-white rounded-full p-2 shadow-md hover:bg-primary-dark transition-colors duration-200 z-10"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-col md:flex-row justify-between gap-4 mt-auto">
              <p className="text-red-500 max-w-full md:max-w-[75%] text-sm md:text-base">
                {showErrorMessage &&
                  "Veuillez sélectionner la ou les bonnes réponses avant de continuer. "}
              </p>
              <Button
                label="Suivant"
                className="btn-primary text-sans h-[40px] md:h-[50px] min-w-fit focus:outline-none focus:ring-0"
                onClick={handleNextQuestion}
              />
            </div>
          </div>
          <div className="w-full lg:w-1/2 lg:pl-4 mt-4 lg:mt-0">
            <div className="h-full flex items-center justify-center">
              {currentQuestion.fileName ? (
                <img
                  src={fileService.getFile(currentQuestion.fileName)}
                  alt="Question"
                  className="max-w-full max-h-[300px] lg:max-h-full rounded-md overflow-clip object-contain"
                />
              ) : (
                <img
                  src={emptyImage}
                  alt="Question"
                  className="max-w-full max-h-[300px] lg:max-h-full rounded-md overflow-clip object-contain"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="relative mt-4">
        <div
          id="chapter-progress"
          className="bg-white rounded-lg p-4 md:p-6 border border-gray-200 collapsible-progress"
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-8 pe-2">
            <h3 className="text-base md:text-lg font-semibold m-0 p-0">
              Progression globale
            </h3>
            <div className="flex-1 bg-neutral-200 rounded-full h-2.5 w-full md:w-auto">
              <div
                className="bg-primary h-2.5 rounded-full"
                style={{ width: `${overallProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-neutral-600 text-left md:text-right">
              {completedChapters} sur {chapters.length} chapitres terminés
            </p>
          </div>
          <div className="progress-content">
            <p className="text-sm text-neutral-600 my-4 text-center">
              {Math.round(overallProgress)}% Terminé
            </p>
            <div className="flex flex-wrap justify-center md:justify-evenly gap-4 mb-4">
              {chapters.map((chapter, index) => {
                const driver = authService.getUser();
                const isCompleted = driver.completedChapters?.includes(
                  chapter._id
                );
                return (
                  <div
                    key={chapter._id}
                    className="rounded-lg border border-neutral-300 py-2 px-3 w-full md:w-[200px] flex gap-2 items-center"
                  >
                    <div className="">
                      {isCompleted ? (
                        <svg
                          className="w-4 h-4 p-[1px] text-primary mx-auto border-2 border-primary rounded-full bg-primary"
                          fill="black"
                          stroke="white"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="4"
                            d="M5 13l4 4L19 7"
                          ></path>
                        </svg>
                      ) : (
                        <svg
                          className="w-4 h-4 p-[1px] text-neutral-600 mx-auto border-2 border-neutral-600 rounded-full"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="4"
                            d="M6 18L18 6M6 6l12 12"
                          ></path>
                        </svg>
                      )}
                    </div>
                    <p className="font-semibold text-sm">{chapter.title}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default TestPage;
